/**
 * Fetch a bearer-protected file as a blob and expose it as an object URL
 * (for an <iframe>, <img> or <embed> src). A bare URL on those elements sends
 * no token and lands on {"detail":"Not authenticated"}, so the bytes go
 * through the api client (services/authedFile) first.
 *
 * The object URL is revoked when the path changes or the owner unmounts.
 */
import { useEffect, useState } from 'react';
import { fetchAuthedBlob } from '../services/authedFile';

export interface AuthedObjectUrlState {
  url: string | null;
  loading: boolean;
  error: string | null;
}

const IDLE: AuthedObjectUrlState = { url: null, loading: false, error: null };
const LOADING: AuthedObjectUrlState = { url: null, loading: true, error: null };

/** A finished fetch, keyed by the path it answers so a stale one never shows. */
interface Loaded {
  path: string;
  url: string | null;
  error: string | null;
}

export function useAuthedObjectUrl(path: string | null): AuthedObjectUrlState {
  const [loaded, setLoaded] = useState<Loaded | null>(null);

  useEffect(() => {
    if (!path) return;
    let cancelled = false;
    let objectUrl: string | null = null;
    fetchAuthedBlob(path)
      .then((blob) => {
        if (cancelled) return;
        objectUrl = URL.createObjectURL(blob);
        setLoaded({ path, url: objectUrl, error: null });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setLoaded({
          path,
          url: null,
          error: err instanceof Error && err.message ? err.message : 'Failed to load file',
        });
      });
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [path]);

  if (!path) return IDLE;
  if (!loaded || loaded.path !== path) return LOADING;
  return { url: loaded.url, loading: false, error: loaded.error };
}
